import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Calendar, Users, TrendingUp, KeyRound, AlertTriangle } from 'lucide-react';
import { CreateGameModal } from '@/components/games/CreateGameModal';
import { GameCodeModal } from '@/components/games/GameCodeModal';
import { GameCard } from '@/components/games/GameCard';
import { GameRosterManagement } from '@/components/games/GameRosterManagement';
import { CoachRosterManagement } from '@/components/games/CoachRosterManagement';
import { LeaderboardDisplay } from '@/components/games/LeaderboardDisplay';
import { PlayerRequestNotifications } from '@/components/notifications/PlayerRequestNotifications';
import { useGameManagement } from '@/hooks/useGameManagement';
import { useGameRealTimeManager } from '@/hooks/useGameRealTimeManager';
import { useUserRole } from '@/hooks/useUserRole';
import { toast } from 'sonner';
import type { GameWithDetails } from '@/types/gameSystem';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export const GameManagementDashboard: React.FC = () => {
  const [selectedGame, setSelectedGame] = useState<GameWithDetails | null>(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showGameCodeModal, setShowGameCodeModal] = useState(false);
  
  const { permissions, userId } = useUserRole();
  const { games, loading, confirmGame, updateGame, refreshGames } = useGameManagement(userId);

  // Comprehensive real-time game management
  useGameRealTimeManager({
    userId,
    onGameUpdate: () => {
      console.log('Game updated - data will refresh automatically');
      refreshGames();
    },
    onRosterUpdate: () => {
      toast.info('Roster updated!');
      refreshGames();
    },
    onPlayerStatusUpdate: () => {
      console.log('Player status updated');
      refreshGames();
    },
    onConfirmationUpdate: () => {
      toast.success('Team confirmation updated!');
      refreshGames();
    },
    onGameStatusUpdate: () => {
      toast.info('Game status updated!');
      refreshGames();
    }
  });

  const upcomingGames = games.filter(g => {
    const gameDateTime = new Date(`${g.game_date} ${g.game_time || '00:00'}`);
    const now = new Date();
    
    const isFutureGame = gameDateTime > now;
    const validStatuses = ['confirmed', 'pending', 'approved'];
    
    console.log('🎮 Filtering upcoming game:', {
      game_name: g.game_name,
      game_status: g.game_status,
      game_date: g.game_date,
      isFutureGame,
      validStatus: validStatuses.includes(g.game_status),
      home_team: g.home_team?.team_name || 'TBD',
      away_team: g.away_team?.team_name || 'TBD'
    });
    
    return validStatuses.includes(g.game_status) && isFutureGame;
  });

  const activeGames = games.filter(g => g.game_status === 'in_progress');
  const completedGames = games.filter(g => ['completed', 'cancelled', 'forfeited'].includes(g.game_status));

  const missedGames = games.filter(g => {
    const gameDateTime = new Date(`${g.game_date} ${g.game_time || '00:00'}`);
    const now = new Date();
    
    // Games are considered "missed" if they've started and are still in pending/confirmed/approved status
    // This means the game time has passed but the game hasn't been marked as completed or forfeited
    const isMissed = gameDateTime < now && 
                     ['confirmed', 'pending', 'approved'].includes(g.game_status);
    
    console.log('🎮 Filtering missed game:', {
      game_name: g.game_name,
      game_date: g.game_date,
      game_time: g.game_time,
      gameDateTime: gameDateTime.toISOString(),
      now: now.toISOString(),
      game_status: g.game_status,
      isMissed,
      validStatus: ['confirmed', 'pending', 'approved'].includes(g.game_status)
    });
    
    return isMissed;
  });

  // Log all game counts for debugging
  console.log('🎮 Game counts:', {
    total: games.length,
    upcoming: upcomingGames.length,
    active: activeGames.length,
    completed: completedGames.length,
    missed: missedGames.length
  });

  const handleConfirmGame = async (game: GameWithDetails, teamSide: 'home' | 'away') => {
    if (!userId) return;
    
    const teamToConfirm = teamSide === 'home' ? game.home_team : game.away_team;
    const teamId = teamSide === 'home' ? game.home_team_id : game.away_team_id;
    
    if (!teamToConfirm || !teamId) {
      toast.error(`No ${teamSide} team assigned to confirm`);
      return;
    }
    
    try {
      await confirmGame(game.id, teamSide, teamId);
      toast.success(`${teamSide === 'home' ? 'Home' : 'Away'} team confirmed successfully`);
    } catch (error) {
      console.error('Error confirming team:', error);
      toast.error('Failed to confirm team');
    }
  };

  const handleAssignTeam = async (teamSide: 'home' | 'away', teamId: number, teamName: string) => {
    if (!userId) {
      toast.error('Please log in to assign teams');
      return;
    }

    const gameToUpdate = upcomingGames.find(g => 
      (teamSide === 'home' && !g.home_team_id) || 
      (teamSide === 'away' && !g.away_team_id)
    );

    if (!gameToUpdate) {
      toast.error('Game not found or team already assigned');
      return;
    }

    try {
      const updateData = teamSide === 'home' 
        ? { home_team_id: teamId }
        : { away_team_id: teamId };

      const success = await updateGame(gameToUpdate.id, updateData);
      
      if (success) {
        toast.success(`${teamName} assigned as ${teamSide} team`);
        await refreshGames();
      } else {
        toast.error('Failed to assign team');
      }
    } catch (error) {
      console.error('Error assigning team:', error);
      toast.error('Failed to assign team');
    }
  };

  return (
    <div className="space-y-6">
      {/* Player Request Notifications for Coaches/Team Owners */}
      <PlayerRequestNotifications />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Games Dashboard</h2>
          <p className="text-gray-400">Manage your team's games, rosters, and track performance</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Button
            onClick={() => setShowGameCodeModal(true)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <KeyRound className="h-4 w-4" />
            Join Game
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap w-full gap-2 bg-gray-800 border border-gray-700 p-3 h-auto">
          <TabsTrigger value="upcoming" className="text-white data-[state=active]:bg-blue-600 flex items-center justify-center gap-2 px-4 py-2 rounded-lg flex-1 min-w-[140px]">
            <Calendar className="h-4 w-4" />
            Upcoming ({upcomingGames.length})
          </TabsTrigger>
          <TabsTrigger value="missed" className="text-white data-[state=active]:bg-red-600 flex items-center justify-center gap-2 px-4 py-2 rounded-lg flex-1 min-w-[140px]">
            <AlertTriangle className="h-4 w-4" />
            Missed ({missedGames.length})
          </TabsTrigger>
          <TabsTrigger value="active" className="text-white data-[state=active]:bg-green-600 flex items-center justify-center gap-2 px-4 py-2 rounded-lg flex-1 min-w-[140px]">
            <Trophy className="h-4 w-4" />
            Active ({activeGames.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="text-white data-[state=active]:bg-gray-600 flex items-center justify-center gap-2 px-4 py-2 rounded-lg flex-1 min-w-[140px]">
            <Users className="h-4 w-4" />
            Completed ({completedGames.length})
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="text-white data-[state=active]:bg-purple-600 flex items-center justify-center gap-2 px-4 py-2 rounded-lg w-full">
            <TrendingUp className="h-4 w-4" />
            Leaderboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-4">
          {loading ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <div className="text-gray-400">Loading games...</div>
              </CardContent>
            </Card>
          ) : upcomingGames.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <Trophy className="h-12 w-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">No upcoming games</p>
                <p className="text-sm text-gray-500 mt-2">
                  {permissions.canCreateGames 
                    ? "Create a new game to get started!" 
                    : "Games you create or participate in will appear here."
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {upcomingGames.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  currentUserId={userId}
                  onConfirm={(teamSide) => handleConfirmGame(game, teamSide)}
                  onManageRoster={() => setSelectedGame(game)}
                  onAssignTeam={handleAssignTeam}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="active" className="space-y-4">
          {activeGames.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <p className="text-gray-400">No active games</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {activeGames.map((game) => (
                <GameCard key={game.id} game={game} currentUserId={userId} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="missed" className="space-y-4">
          {missedGames.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <AlertTriangle className="h-12 w-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">No missed games</p>
                <p className="text-sm text-gray-500 mt-2">
                  Games that have passed their scheduled time will appear here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {missedGames.map((game) => (
                <div key={game.id} className="relative">
                  <div className="absolute top-2 right-2 z-10">
                    <Badge variant="destructive" className="bg-red-600 text-white">
                      MISSED
                    </Badge>
                  </div>
                  <GameCard game={game} currentUserId={userId} showActions={false} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedGames.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <p className="text-gray-400">No completed games</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {completedGames.map((game) => (
                <GameCard key={game.id} game={game} currentUserId={userId} showActions={false} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="leaderboard">
          <LeaderboardDisplay />
        </TabsContent>
      </Tabs>

      {selectedGame && (
        permissions.isCoach || permissions.isTeamOwner ? (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <CoachRosterManagement
                isOpen={!!selectedGame}
                onClose={() => setSelectedGame(null)}
                gameId={selectedGame.id || ''}
                currentUserId={userId!}
                game={{
                  game_name: selectedGame.game_name,
                  game_code: selectedGame.game_code
                }}
              />
            </div>
          </div>
        ) : (
          <Dialog open={!!selectedGame} onOpenChange={() => setSelectedGame(null)}>
            <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden bg-gray-800 border-gray-700">
              <div className="overflow-y-auto max-h-[calc(95vh-8rem)] pr-2">
                <GameRosterManagement
                  game={selectedGame}
                  currentUserId={userId!}
                />
              </div>
            </DialogContent>
          </Dialog>
        )
      )}

      <GameCodeModal 
        isOpen={showGameCodeModal} 
        onClose={() => setShowGameCodeModal(false)} 
        onSuccess={() => {
          refreshGames();
          setShowGameCodeModal(false);
        }}
      />
    </div>
  );
};